import { Button } from "@/components/ui/button";
import { cn } from "@/utils";
import { RotateCcw, Trash2 } from "lucide-react";

type ResourceKind = "workspace" | "canvas" | "chat";
type ParentKind = "workspace" | "canvas";

const KIND_LABELS: Record<ResourceKind, string> = {
  workspace: "Workspace",
  canvas: "Canvas",
  chat: "chat",
};

const PARENT_LABELS: Record<ParentKind, string> = {
  workspace: "Workspace",
  canvas: "Canvas",
};

/**
 * Banner shown when the user navigates to an archived resource (stale tab,
 * old link, archived by a collaborator). Full border + tinted background +
 * leading icon per the design-system alert pattern.
 *
 * When `parentKind` is set, the resource was archived with its parent —
 * restores never cascade upward, so the action restores the PARENT (honestly
 * labeled) via the caller-provided `onRestore`.
 */
export function InTrashBanner({
  kind,
  parentKind,
  onRestore,
  isRestoring,
  className,
}: {
  kind: ResourceKind;
  /** Set when the resource was trashed with its parent; the restore action targets the parent. */
  parentKind?: ParentKind;
  onRestore: () => void;
  isRestoring?: boolean;
  className?: string;
}) {
  const label = parentKind
    ? `This ${KIND_LABELS[kind]} is archived because its ${PARENT_LABELS[parentKind]} was archived.`
    : `This ${KIND_LABELS[kind]} is archived.`;
  const hint = parentKind
    ? `Restore the ${PARENT_LABELS[parentKind]} to make it available again.`
    : "You can restore it or leave it archived.";
  const restoreLabel = parentKind
    ? `Restore ${PARENT_LABELS[parentKind]}`
    : `Restore ${KIND_LABELS[kind]}`;

  return (
    <div
      role="status"
      className={cn(
        "flex items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3",
        className,
      )}
    >
      <Trash2 className="h-4 w-4 shrink-0 text-destructive" />
      <p className="flex-1 min-w-0 text-sm text-foreground">
        {label} <span className="text-muted-foreground">{hint}</span>
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={onRestore}
        disabled={isRestoring}
        className="shrink-0"
      >
        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
        {isRestoring ? "Restoring…" : restoreLabel}
      </Button>
    </div>
  );
}
